import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { useChatStore } from '../../store/chat.store';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useMountTransition } from '../../hooks/useMountTransition';
import { useMe } from '../../hooks/useMe';
import { useQuota } from '../../hooks/useQuota';
import { useThemeStore, type ThemePreference } from '../../store/theme.store';

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

function initials(name: string | null | undefined) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SettingsSheet({ open, onClose }: Props) {
  const { mounted, entered, ref: panelRef } = useMountTransition(open);
  const isMobile = useIsMobile();
  const { data: me } = useMe();
  const { data: quotaData } = useQuota();

  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  const setConversations = useChatStore((s) => s.setConversations);
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);
  const setMessages = useChatStore((s) => s.setMessages);

  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClearHistory() {
    setClearing(true);
    setError(null);
    try {
      const res = await apiFetch('/api/chat/conversations', {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Could not clear history');
      setConversations([]);
      setActiveConversation(null);
      setMessages([]);
      setConfirming(false);
    } catch {
      setError('Could not clear history. Please try again.');
    } finally {
      setClearing(false);
    }
  }

  if (!mounted) return null;

  const quota = quotaData?.quota ?? null;
  const planName = quotaData?.plan?.name ?? null;
  const usedPct =
    quota && quota.limit > 0
      ? Math.min(100, Math.round((quota.used / quota.limit) * 100))
      : 0;

  // Shared between both layouts. The drag handle and the "Settings" heading are
  // mobile affordances — the popover is already anchored to the menu that opened
  // it, so on wider screens they're just noise.
  const content = (
    <>
      {isMobile && (
        <>
          <div className="flex shrink-0 justify-center pt-3 pb-1">
            <div className="bg-ink/25 h-1 w-10 rounded-full" />
          </div>
          <h2 className="text-ink px-4 py-3 text-lg font-semibold">Settings</h2>
        </>
      )}

      {/* Identity */}
      <div className="p-4">
        <div className="border-ink/10 flex items-center gap-3 rounded-lg border p-3">
          <span className="bg-ink/50 text-surface-elevated flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-medium">
            {initials(me?.name)}
          </span>
          <div className="min-w-0">
            <p className="text-ink truncate text-sm font-semibold">
              {me?.name ?? '—'}
            </p>
            <p className="text-ink/70 truncate text-sm">
              {me?.netsuiteRoleName ??
                (me?.role === 'admin' ? 'Administrator' : 'User')}
            </p>
            <p className="text-ink/40 truncate text-xs">
              {me?.hostAccountId ?? ''}
            </p>
          </div>
        </div>
      </div>

      {/* Theme */}
      <div className="border-ink/8 flex items-center justify-between gap-3 border-t px-4 py-4">
        <span className="text-ink text-sm">Theme</span>
        <div
          role="radiogroup"
          aria-label="Theme"
          className="border-ink/20 flex shrink-0 overflow-hidden rounded border"
        >
          {THEME_OPTIONS.map((opt) => {
            const active = preference === opt.value;
            return (
              <button
                key={opt.value}
                role="radio"
                aria-checked={active}
                onClick={() => setPreference(opt.value)}
                className={`border-ink/20 border-l px-3 py-1.5 text-sm first:border-l-0 sm:px-4 ${
                  active
                    ? 'bg-surface-elevated text-ink font-semibold'
                    : 'bg-ink/5 text-ink/60'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Plan and token usage */}
      <div className="border-ink/8 border-t px-4 py-4">
        <p className="text-ink text-sm">Plan and token usage</p>
        {quota ? (
          <>
            <div className="bg-ink/10 mt-3 h-2 w-full overflow-hidden rounded-full">
              <div
                className={`bg-danger h-full rounded-full`}
                style={{ width: `${usedPct}%` }}
              />
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2">
              <span className="text-ink/40 text-xs">
                {quota.used.toLocaleString()} / {quota.limit.toLocaleString()}{' '}
                tokens
              </span>
              {planName && (
                <span className="text-ink/60 text-xs">{planName}</span>
              )}
            </div>
          </>
        ) : (
          <p className="text-ink/40 mt-2 text-xs">
            Usage tracking is not available on this plan.
          </p>
        )}
      </div>

      {/* Clear conversation history */}
      <div className="border-ink/8 border-t px-4 py-2">
        {confirming ? (
          <div className="py-2">
            <p className="text-ink text-sm">
              Delete all conversations? This cannot be undone.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleClearHistory}
                disabled={clearing}
                className="bg-danger rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
              >
                {clearing ? 'Clearing…' : 'Yes, clear'}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={clearing}
                className="border-ink/20 text-ink rounded border px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
            </div>
            {error && <p className="text-danger mt-2 text-xs">{error}</p>}
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            className="text-danger flex w-full items-center gap-3 py-3 text-sm"
          >
            <Trash2 size={18} />
            Clear conversation history
          </button>
        )}
      </div>
    </>
  );

  // Mobile: bottom sheet over a dimmed backdrop.
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col justify-end">
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${
            entered ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={onClose}
          aria-hidden="true"
        />

        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Settings"
          className={`bg-surface-elevated relative z-10 flex max-h-[85%] flex-col overflow-y-auto shadow-xl transition-transform duration-200 ${
            entered ? 'translate-y-0 ease-out' : 'translate-y-full ease-in'
          }`}
        >
          {content}
        </div>
      </div>
    );
  }

  // Tablet / desktop: popover anchored under the header, aligned to the menu
  // that opens it. The backdrop stays transparent — it only exists to catch the
  // outside click, since a popover shouldn't dim the app behind it.
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-label="Settings"
        className={`border-ink/10 bg-surface-elevated absolute top-18 right-4 z-10 flex max-h-[calc(100%-6rem)] w-80 origin-top-right flex-col overflow-y-auto rounded-lg border shadow-xl transition duration-200 ${
          entered
            ? 'scale-100 opacity-100 ease-out'
            : 'scale-95 opacity-0 ease-in'
        }`}
      >
        {content}
      </div>
    </div>
  );
}
